import React, {useEffect, useMemo, useState} from "react";
import {
    AssociationReadWReferenceLoad,
    AssociationReadWReferenceLoadAndTagInfo,
    DocumentReadWReferences,
    DocumentRequestRead,
    mergeAssociationsForRequest,
    ReferenceType,
    TagType,
} from "../../../schemas";
import {requiresAssociationConsistency} from "../../../schemas/tag";
import {
    getRelevanceLevelFromScore,
    RelevanceDefaults,
    RelevanceLevel,
} from "../../../routines/schemas";
import {applyTag} from "../../../api";
import Button from "../../SharedComponents/Button/Button";
import {useGroupContext} from "../../../contexts/group_context";
import {useTagContext} from "../../../contexts/tag_context";
import {useEnhancedDocumentsContext} from "../../../contexts/doc_contexts";
import {useNavigationContext} from "../../../contexts/NavContext/nav-context";
import {calculateScoreColor, ThemeColors,} from "../../../colors";
import {BsStars} from "react-icons/bs";
import {withApiErrorHandler} from "../../../error_handling";
import {ReferenceReadWText} from "../../../schemas/reference";
import {useDocumentSpotlight} from "../../../contexts/document_spotlight_context";
import {TAG_UPDATE_COMPLETED} from "../../../events";

import "./index.css";

// ───────────────────────────────────────────
// Shared UI Elements
// ───────────────────────────────────────────

const Pill: React.FC<{
    tone: "neutral" | "good" | "bad";
    children: React.ReactNode;
}> = ({tone, children}) => {
    const cls =
        tone === "good"
            ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
            : tone === "bad"
                ? "bg-rose-50 text-rose-700 ring-rose-200"
                : "bg-gray-50 text-gray-700 ring-gray-200";
    return (
        <span
            className={`inline-flex items-center rounded-full px-2 py-1 text-[11px] font-medium ring-1 ${cls}`}
        >
      {children}
    </span>
    );
};

const RowShell: React.FC<{ children: React.ReactNode }> = ({children}) => (
    <div
        className="relative grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-md border bg-white px-2 py-1 text-xs hover:bg-slate-50">
        {children}
    </div>
);

const SectionHeader: React.FC<{ title: string }> = ({title}) => (
    <div className="mb-1 mt-3 text-sm font-semibold text-slate-800">{title}</div>
);

const ToggleExplanationButton: React.FC<{
    isOpen: boolean;
    onToggle: () => void;
}> = ({isOpen, onToggle}) => (
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();
            onToggle();
        }}
        className={`text-[11px] rounded px-1.5 py-0.5 ring-1 ${
            isOpen
                ? "text-blue-700 bg-blue-50 ring-blue-200"
                : "text-blue-700 hover:bg-blue-50 ring-blue-200"
        }`}
    >
        {isOpen ? "Hide Explanation" : "Show Explanation"}
    </button>
);

const ExplanationBubble: React.FC<{
    header: string;
    body: React.ReactNode;
}> = ({header, body}) => (
    <div className="col-span-3 mt-1">
        <div className="rounded-md border border-blue-200 bg-blue-50/60 px-2 py-1 text-xs">
            <div className="mb-1 font-semibold text-blue-900">{header}</div>
            <div className="text-slate-700">{body}</div>
        </div>
    </div>
);


const RELEVANCE_ORDER: RelevanceLevel[] = ["Hot", "Warm", "Cold"];

/**
 * Cycles through relevance levels: Hot → Warm → Cold → Hot,
 * and returns the default representative score for the next level.
 */
export const getNextScore = (currentScore: number): number => {
    const currentLevel = getRelevanceLevelFromScore(currentScore);
    const currentIndex = RELEVANCE_ORDER.indexOf(currentLevel);
    const nextIndex = (currentIndex + 1) % RELEVANCE_ORDER.length;
    const nextLevel = RELEVANCE_ORDER[nextIndex];
    return RelevanceDefaults[nextLevel];
};

export const ScoreBadge: React.FC<{
    score?: number;
    assoc: AssociationReadWReferenceLoadAndTagInfo;
    editable?: boolean;
    onScoreChange?: (newScore: number) => void;
}> = ({score, editable = false, onScoreChange}) => {
    const [isFlipping, setIsFlipping] = React.useState(false);

    const label = getRelevanceLevelFromScore(score);
    const color = calculateScoreColor(score ?? -1.0);

    React.useEffect(() => {
        if (!editable) return;
        setIsFlipping(true);
        const t = setTimeout(() => setIsFlipping(false), 350);
        return () => clearTimeout(t);
    }, [score]);

    return (
        <button
            type="button"
            disabled={!editable}
            onClick={(e) => {
                e.stopPropagation();
                if (editable && onScoreChange) {
                    const next = getNextScore(score ?? -1.0);
                    onScoreChange(next);
                }
            }}
            title={editable ? "Click to change relevance level" : undefined}
            className={`rounded-full px-2 py-1 text-[11px] font-medium text-white transition-transform duration-300 ${
                editable ? "cursor-pointer hover:scale-105" : "cursor-default"
            } ${isFlipping ? "animate-flip-once" : ""}`}
            style={{backgroundColor: color}}
        >
            {label}
            {editable && <span className="ml-1 text-[13px] opacity-70">✎</span>}
        </button>
    );
};

// ───────────────────────────────────────────
// Helper to build phrases from references
// ───────────────────────────────────────────

interface PhraseWithMeta {
    phrase?: string;
    explanation?: string;
    color: string;
    referenceType: ReferenceType;
    pageHint?: number;
}

interface AssociationPhrases {
    phrasesWithQuotes: PhraseWithMeta[];
    explanationsOnly: PhraseWithMeta[];
    allExplanations: PhraseWithMeta[];
}

const buildPhrasesFromAssociation = (
    assoc: AssociationReadWReferenceLoadAndTagInfo
): AssociationPhrases => {
    const phrasesWithQuotes: PhraseWithMeta[] = [];
    const explanationsOnly: PhraseWithMeta[] = [];

    for (const ref of assoc.references as ReferenceReadWText[]) {
        const opacity = Math.max(0.5, Math.min(1, Math.abs(assoc.score ?? 0)));
        const color = `rgba(180,180,255,${opacity})`;
        const item: PhraseWithMeta = {
            phrase: ref.referenced_text || undefined,
            explanation: ref.explanation,
            color,
            referenceType: ref.reference_type,
            pageHint: ref.page_start ?? undefined,
        };

        if (ref.referenced_text) {
            phrasesWithQuotes.push(item);
        } else if (ref.explanation) {
            explanationsOnly.push(item);
        }
    }

    return {
        phrasesWithQuotes,
        explanationsOnly,
        allExplanations: [...phrasesWithQuotes, ...explanationsOnly],
    };
};

// ───────────────────────────────────────────
// Row Component
// ───────────────────────────────────────────

interface TagAssociationRowProps {
    assoc: AssociationReadWReferenceLoadAndTagInfo;
    tagId: string;
    tagType: TagType;
    editingEnabled: boolean;
    onScoreChange: (id: string, newScore: number) => void;
    isOpen: boolean;
    toggleExplanation: (id: string) => void;
}

const TagAssociationRow: React.FC<TagAssociationRowProps> = ({
                                                                 assoc,
                                                                 tagId,
                                                                 editingEnabled,
                                                                 onScoreChange,
                                                                 isOpen,
                                                                 toggleExplanation,
                                                             }) => {
    const {phrasesWithQuotes, allExplanations} = buildPhrasesFromAssociation(assoc);
    const spotlightCtx = useDocumentSpotlight();

    const relevanceLevel = getRelevanceLevelFromScore(assoc.score);
    const isNegativeLabel = relevanceLevel === "Cold";
    const explanationLabel = isNegativeLabel ? "Why not relevant:" : "Why relevant:";

    const handleToggleExplanation = () => {
        const willOpen = !isOpen;
        toggleExplanation(tagId);
        if (willOpen && spotlightCtx) {
            const tagName = assoc.tagName ?? tagId;
            for (const p of phrasesWithQuotes) {
                if (p.phrase) {
                    spotlightCtx.toggleOrAddSpotlight({text: p.phrase, source: `Tag: ${tagName}`, pageHint: p.pageHint});
                }
            }
        }
    };

    const handleViewQuotes = () => {
        if (!spotlightCtx) return;
        const tagName = assoc.tagName ?? tagId;
        const phrasesWithHints = phrasesWithQuotes.filter(p => p.phrase);
        if (phrasesWithHints.length === 0) return;
        spotlightCtx.jumpToSpotlight({text: phrasesWithHints[0].phrase!, source: `Tag: ${tagName}`, pageHint: phrasesWithHints[0].pageHint});
        for (const p of phrasesWithHints.slice(1)) {
            spotlightCtx.toggleOrAddSpotlight({text: p.phrase!, source: `Tag: ${tagName}`, pageHint: p.pageHint});
        }
    };

    const handleQuoteClick = (quoteText: string) => {
        if (spotlightCtx) {
            const phrase = phrasesWithQuotes.find(p => p.phrase === quoteText);
            spotlightCtx.jumpToSpotlight({text: quoteText, source: `Tag: ${assoc.tagName ?? tagId}`, pageHint: phrase?.pageHint});
        }
    };

    const hasExplanations = allExplanations.length > 0;
    const hasQuotes = phrasesWithQuotes.length > 0;

    return (
        <>
            <RowShell>
                <div className="text-sm font-medium text-left text-gray-800">
                    {assoc.tagName ?? tagId}
                </div>
                <div className="text-right">
                    <ScoreBadge
                        score={assoc.score}
                        assoc={assoc}
                        editable={editingEnabled}
                        onScoreChange={(newScore) => onScoreChange(tagId, newScore)}
                    />
                </div>
                {(hasExplanations || hasQuotes) && (
                    <div className="flex justify-end gap-1">
                        {hasExplanations && (
                            <ToggleExplanationButton
                                isOpen={isOpen}
                                onToggle={handleToggleExplanation}
                            />
                        )}
                        {hasQuotes && (
                            <button
                                type="button"
                                className="text-[11px] underline text-blue-700 hover:text-blue-800"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewQuotes();
                                }}
                            >
                                View quotes
                            </button>
                        )}
                    </div>
                )}
            </RowShell>

            {isOpen && (
                <ExplanationBubble
                    header={`${assoc.tagName ?? tagId} — Explanation`}
                    body={
                        hasExplanations ? (
                            <div className="space-y-1">
                                {allExplanations.map((p, i) => (
                                    <div key={i} className="whitespace-pre-wrap">
                                        {p.phrase && (
                                            <div
                                                className="italic text-slate-800 mb-0.5 cursor-pointer hover:text-blue-700 hover:underline"
                                                onClick={() => handleQuoteClick(p.phrase!)}
                                                title="Click to spotlight this quote"
                                            >
                                                {`\u201C${p.phrase}\u201D`}
                                            </div>
                                        )}
                                        {p.explanation && (
                                            <div className="text-xs text-slate-600 mt-0.5">
                                                <span className="font-medium">{explanationLabel}</span>{" "}
                                                {p.explanation}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-slate-700">
                                No explanation available.
                            </div>
                        )
                    }
                />
            )}
        </>
    );
};

// ───────────────────────────────────────────
// Main Component
// ───────────────────────────────────────────

const DocumentToolsTagging: React.FC<{
    doc: DocumentReadWReferences;
}> = ({doc}) => {
    const {tagMap, refreshTags, documentRequests} = useTagContext()!;
    const {currentGroup} = useGroupContext()!;
    const {refreshDocumentField} = useEnhancedDocumentsContext()!;
    const {navigateTo} = useNavigationContext()!;

    const [associationMap, setAssociationMap] = useState<
        Map<string, AssociationReadWReferenceLoadAndTagInfo>
    >(new Map());
    const [openExplanations, setOpenExplanations] = useState<Set<string>>(
        new Set()
    );
    const [editingEnabled, setEditingEnabled] = useState(false);
    const [loading, setLoading] = useState(false);

    const toggleExplanation = (id: string) =>
        setOpenExplanations((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const buildAssociations = React.useCallback(() => {
        const map = new Map<string, AssociationReadWReferenceLoadAndTagInfo>();
        for (const a of (doc.applied_tag_associations ??
            []) as AssociationReadWReferenceLoad[]) {
            const tag = tagMap.get(a.tag_id);
            if (!tag) continue;
            map.set(a.tag_id, {
                ...a,
                tagName: tag.tag_name,
                tagType: tag.type,
            });
        }
        setAssociationMap(map);
    }, [doc, tagMap]);

    useEffect(() => {
        buildAssociations();
    }, [buildAssociations]);

    // When a tag-update job finishes, the doc has new applied_tag_associations
    // on the server. doc_contexts refreshes the grid, but this doc prop won't
    // update unless we re-pull its associations field explicitly.
    useEffect(() => {
        const onTagUpdated = async () => {
            await refreshDocumentField(doc, "applied_tag_associations");
        };
        window.addEventListener(TAG_UPDATE_COMPLETED, onTagUpdated);
        return () => window.removeEventListener(TAG_UPDATE_COMPLETED, onTagUpdated);
    }, [doc, refreshDocumentField]);

    const handleScoreChange = (id: string, newScore: number) => {
        setAssociationMap((prev) => {
            const next = new Map(prev);
            const assoc = next.get(id);
            if (assoc) next.set(id, {...assoc, score: newScore});
            return next;
        });
    };

    const handleClearEdits = () => buildAssociations();

    const handleSaveChanges = async () => {
        setLoading(true);
        for (const [id, assoc] of associationMap.entries()) {
            const operatorLabeled = true;
            const newScore = assoc.score;
            await withApiErrorHandler(
                () =>
                    applyTag(
                        id,
                        operatorLabeled,
                        newScore!,
                        doc.id,
                        currentGroup!
                    ),
                {titleQualifier: "Error Applying Tag", navigateTo}
            );
        }
        await refreshTags(Array.from(associationMap.keys()));
        await refreshDocumentField(doc, "applied_tag_associations");
        setLoading(false);
        setEditingEnabled(false);
    };

    interface DocRequestWithChildren {
        docRequest: DocumentRequestRead;
        parentAssoc: AssociationReadWReferenceLoadAndTagInfo;
        childTags: AssociationReadWReferenceLoadAndTagInfo[];
    }

    const docRequestsWithChildren = useMemo(() => {
        if (!Array.isArray(documentRequests) || !associationMap.size) return [];
        return documentRequests
            .map((dr) => {
                const childTags = dr.tags
                    .map((t) => associationMap.get(t.id))
                    .filter(Boolean) as AssociationReadWReferenceLoadAndTagInfo[];
                if (!childTags.length) return null;
                return {
                    docRequest: dr,
                    parentAssoc: mergeAssociationsForRequest(dr, childTags),
                    childTags,
                };
            })
            .filter(Boolean) as DocRequestWithChildren[];
    }, [associationMap, documentRequests]);

    const responsiveSection = (
        <>
            <SectionHeader title="Responsive"/>
            {docRequestsWithChildren.map(({docRequest, parentAssoc, childTags}) => (
                <div key={docRequest.id} className="mb-3 space-y-2">
                    <RowShell>
                        <div className="text-sm font-semibold text-gray-800">
                            {docRequest.name}
                        </div>
                        <div className="text-right">
                            <ScoreBadge score={parentAssoc.score} assoc={parentAssoc}/>
                        </div>
                        <div className="flex justify-end gap-1">
                            {parentAssoc.operator_labeled ?
                                <Pill tone="good">Manual</Pill> :
                                <Pill tone="neutral">Inferred</Pill>}
                        </div>
                    </RowShell>

                    <div className="pl-4 space-y-2 border-l border-slate-200">
                        {childTags.map((assoc) => (
                            <TagAssociationRow
                                key={assoc.tag_id}
                                assoc={assoc}
                                tagId={assoc.tag_id}
                                tagType={TagType.REQUEST_SET}
                                editingEnabled={editingEnabled}
                                onScoreChange={handleScoreChange}
                                isOpen={openExplanations.has(assoc.tag_id)}
                                toggleExplanation={toggleExplanation}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </>
    );

    // Filter to scoring-pipeline tags only (excludes exchange type tags shown in TranscriptAnalysisView)
    const codeTags = Array.from(associationMap.values()).filter((a) => {
        const tag = tagMap.get(a.tag_id);
        if (!tag) return a.tagType === TagType.TAG;
        return requiresAssociationConsistency(tag) && a.tagType === TagType.TAG;
    });

    const codesSection = (
        <>
            <SectionHeader title="Codes"/>
            <div className="space-y-2">
                {codeTags.map((assoc) => (
                    <TagAssociationRow
                        key={assoc.tag_id}
                        assoc={assoc}
                        tagId={assoc.tag_id}
                        tagType={TagType.TAG}
                        editingEnabled={editingEnabled}
                        onScoreChange={handleScoreChange}
                        isOpen={openExplanations.has(assoc.tag_id)}
                        toggleExplanation={toggleExplanation}
                    />
                ))}
            </div>
        </>
    );
    return (
        <div className="flex flex-col space-y-4">
            {(!!documentRequests?.length || codeTags.length > 0) && (
                <div className="flex items-center justify-end font-semibold text-gray-700">
                    <BsStars className="w-[1.25em] h-[1.25em]" color={ThemeColors.colorContrastLightHex}/>
                    <span className="ml-2">Loa&apos;s Relevance Score</span>
                </div>
            )}

            {!!documentRequests?.length && responsiveSection}
            {codeTags.length > 0 &&
                <>
                    <div>{codesSection}</div>
                    <div className="flex justify-end gap-2 pt-2">
                        {!editingEnabled ? (
                            <Button
                                size="wide"
                                title="Enable Edits"
                                additionalClassName="text-xs px-8 py-1"
                                onClick={() => setEditingEnabled(true)}
                            />
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    size="wide"
                                    title="Clear Edits"
                                    additionalClassName="text-xs px-8 py-1"
                                    onClick={handleClearEdits}
                                />
                                <Button
                                    type="button"
                                    size="wide"
                                    title="Save Changes"
                                    additionalClassName="text-xs px-8 py-1"
                                    loading={loading}
                                    onClick={handleSaveChanges}
                                />
                            </>
                        )}
                    </div>
                </>
            }
        </div>
    );
};

export default DocumentToolsTagging;
